// Expert Status Card for VibesPage
// Shows user's expert level, health, and AI visibility status

import React, { useEffect, useState } from 'react';
import { Award, TrendingUp, Eye, Zap, MessageSquare } from 'lucide-react';
import { getExpertStatus, calculateNextMilestone } from '../../services/expertStatusService';
import { ExpertStatus, EXPERT_STATUS_LEVELS } from '../../types/expertStatus.types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

interface PeerInsight {
  reviewerName: string;
  expertiseTags: string[];
  expertTagline: string;
  wouldRecommend: boolean;
  meetingTopic: string;
  meetingDurationMinutes: number | null;
  createdAt: string;
}

interface ExpertStatusCardProps {
  userId: string;
}

const ExpertStatusCard: React.FC<ExpertStatusCardProps> = ({ userId }) => {
  const [status, setStatus] = useState<ExpertStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [peerInsights, setPeerInsights] = useState<PeerInsight[]>([]);

  useEffect(() => {
    loadStatus();
    loadPeerInsights();
  }, [userId]);

  const loadStatus = async () => {
    try {
      const data = await getExpertStatus(userId);
      setStatus(data);
    } catch (error) {
      console.error('Error loading expert status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPeerInsights = async () => {
    try {
      const snap = await getDoc(doc(db, 'expertStatus', userId));
      if (snap.exists()) {
        const insights = snap.data()?.peerInsights as PeerInsight[] | undefined;
        if (Array.isArray(insights)) {
          // Show most recent 3 insights
          setPeerInsights(insights.slice(-3).reverse());
        }
      }
    } catch { /* non-critical */ }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    );
  }

  if (!status) return null;

  const levelConfig = EXPERT_STATUS_LEVELS[status.level];
  const nextMilestone = calculateNextMilestone(status);
  const indexabilityChecks = [
    { label: 'Expert Status bestaat', pass: Boolean(status) },
    { label: 'AI vindbaarheid is ingeschakeld', pass: Boolean(status.aiVisibility?.enabled) },
    { label: 'Expert level is actief', pass: status.level !== 'none' },
  ];
  const isIndexable = indexabilityChecks.every((check) => check.pass);
  
  const levelColors: Record<string, string> = {
    none: 'bg-gray-100 text-gray-700 border-gray-300',
    verified: 'bg-blue-50 text-blue-700 border-blue-200',
    expert: 'bg-purple-50 text-purple-700 border-purple-200',
    top_expert: 'bg-amber-50 text-amber-700 border-amber-200'
  };

  const healthColors: Record<string, string> = {
    healthy: 'bg-emerald-500',
    warning: 'bg-yellow-500',
    urgent: 'bg-orange-500',
    expiring: 'bg-red-500'
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Award className="text-[#7A9488]" size={24} />
          <h3 className="text-lg font-semibold text-gray-800">Expert Status</h3>
        </div>
      </div>

      {/* Current Level */}
      <div className="mb-4">
        <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full border ${levelColors[status.level]}`}>
          <Award size={20} />
          <span className="font-medium">{levelConfig.description}</span>
        </div>
      </div>

      {/* Health Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Status Health</span>
          <span className="text-sm font-semibold text-gray-800">{status.health}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${healthColors[status.healthStatus]}`}
            style={{ width: `${status.health}%` }}
          ></div>
        </div>
        {status.daysUntilDowngrade !== null && status.daysUntilDowngrade <= 7 && (
          <p className="text-xs text-amber-600 mt-1">
            Nog {status.daysUntilDowngrade} dag{status.daysUntilDowngrade !== 1 ? 'en' : ''} tot downgrade
          </p>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800">{status.conversationsLast30Days}</p>
          <p className="text-xs text-gray-600">Gesprekken (30d)</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800">{status.loginStreak}</p>
          <p className="text-xs text-gray-600">Login streak</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800">{status.totalConversations}</p>
          <p className="text-xs text-gray-600">Totaal</p>
        </div>
      </div>

      {/* Next Milestone */}
      {nextMilestone && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <TrendingUp size={16} className="text-blue-500" />
            <span className="text-sm font-medium text-gray-700">Volgende level</span>
          </div>
          <p className="text-xs text-gray-600">
            Nog <strong>{nextMilestone.conversationsNeeded}</strong> gesprek{nextMilestone.conversationsNeeded !== 1 ? 'ken' : ''} nodig voor <strong>{EXPERT_STATUS_LEVELS[nextMilestone.level].description}</strong>
          </p>
        </div>
      )}

      {/* Peer Insights */}
      {peerInsights.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={16} className="text-[#7A9488]" />
            <span className="text-sm font-medium text-gray-700">Aanbevelingen van connecties</span>
          </div>
          <div className="space-y-2">
            {peerInsights.map((insight, i) => (
              <div key={i} className="p-3 bg-[#7A9488]/5 rounded-xl border border-[#7A9488]/15">
                {insight.expertTagline && (
                  <p className="text-xs text-gray-800 font-medium italic mb-1">"{insight.expertTagline}"</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">— {insight.reviewerName}</span>
                  {insight.wouldRecommend && (
                    <span className="text-xs text-emerald-600 font-medium">👍 Aangeraden</span>
                  )}
                </div>
                {insight.expertiseTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {insight.expertiseTags.slice(0, 3).map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-[#7A9488]/15 text-[#4F6660] rounded-full text-xs font-medium">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Visibility */}
      <div className="p-3 bg-gradient-to-r from-stone-50 to-blue-50 rounded-lg border border-stone-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {status.aiVisibility.enabled ? (
              <Eye size={18} className="text-emerald-500" />
            ) : (
              <Eye size={18} className="text-gray-400" />
            )}
            <span className="text-sm font-medium text-gray-700">AI Vindbaarheid</span>
          </div>
          <div className="flex items-center space-x-2">
            {status.aiVisibility.enabled && (
              <Zap size={14} className="text-[#7A9488]" />
            )}
            <span className={`text-sm font-semibold ${status.aiVisibility.enabled ? 'text-emerald-600' : 'text-gray-500'}`}>
              {status.aiVisibility.enabled ? 'Actief' : 'Inactief'}
            </span>
          </div>
        </div>
        {status.aiVisibility.enabled && (
          <p className="text-xs text-gray-600 mt-2">
            Ranking: {status.aiVisibility.searchRanking}/200
          </p>
        )}
      </div>

      <div className="mt-4 p-3 rounded-lg border border-[#E0DDD8] bg-[#F7F5F0]">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-[#1C1C1C]">Indexeerbaarheid</span>
          <span className={`text-xs font-semibold ${isIndexable ? 'text-emerald-600' : 'text-amber-600'}`}>
            {isIndexable ? 'Actief' : 'Inactief'}
          </span>
        </div>
        <ul className="mt-2 space-y-1">
          {indexabilityChecks.map((check) => (
            <li key={check.label} className="text-xs text-[#5A5A5A] flex items-center gap-2">
              <span>{check.pass ? '✓' : '•'}</span>
              <span>{check.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ExpertStatusCard;
